/* eslint-disable newline-per-chained-call */
import { CLASS_UOM_CONVERSION, generateUOMConversionURL, getUOMConversionString, getUOMNameFromUnitOfMeasureCNL, UOM_SUFFIX } from "c8-platform";
import FormData from "csi/action/FormData";
import { FormWidget } from "csi/form/Widget";
import csi from "csi/main";
import reflection from "csi/reflection";
import resolver from "csi/resolver";
import New from "csi/table/New";
import { cnl } from "csi/typings";
import xml from "csi/xml";
import declare from "dojo/_base/declare";
import BusinessObject from "./BusinessObject";
import ConfigurableAttributeMixin from "./ConfigurableAttributeMixin";

const NewUOMAttributes = declare([New, ConfigurableAttributeMixin], {

	COLUMNWIDTH_TYPE: "ColumnWidthType",
	FIELD_EXPRESSION_STATE: "ExpressionState",
	FIELD_NOTES: "Notes",
	FIELD_REFLECTION_ATTRIBUTE_OWNER: "ReflectionAttributeOwner",
	FIELD_REQUIRED: "Required",
	FIELD_UNIT_OF_MEASURE: "UnitOfMeasure",
	FIELD_UNIT_OF_MEASURE_ENABLED: "UnitOfMeasureEnabled",
	FIELD_UOM_TYPE: "UOMType",
	FIELD_VALIDATION_EXPRESSION: "ValidationExpression",

	/**
	 * @override
	 * Get nodes resulting from action for postprocessing
	 */
	getNewNodes() {
		const fd: FormData = this.m_formData;
		// TODO: form data will be not defined in case of using React
		if (fd) {
			const uomForm = fd.getFieldValue(this.FIELD_UNIT_OF_MEASURE_ENABLED);
			if (uomForm) {
				const uomValue = resolver.resolveNode(this.m_urlNode, false, this.m_cache);
				const uomConversion = resolver.resolveNode(getUOMConversionString(this.m_urlNode), false, this.m_cache);

				return [uomValue, uomConversion];
			}
		}

		return this.inherited(arguments);
	},

	/**
	 * @override
	 * Remove "Node Name" from automatic ChangeNode
	 */
	toWriterFields(urlNode: cnl): void {
		const fd: FormData = this.m_formData;
		const uomForm = fd.getFieldValue(this.FIELD_UNIT_OF_MEASURE_ENABLED);
		if (uomForm) {
			fd.getFieldValue(xml.ATTR_NAME, true);
		}
		this.inherited(arguments);
	},

	/**
	 * @override
	 * Create UOM custom attributes and add them to the business object
	 */
	toWriterBase(url: string): boolean {
		const fd: FormData = this.m_formData;
		const uomForm = fd.getFieldValue(this.FIELD_UNIT_OF_MEASURE_ENABLED);
		if (!uomForm) {
			return this.inherited(arguments) as unknown as boolean;
		}
		const value = fd.getFieldValue(reflection.ATTR_VALUE, true);
		const uomType = fd.getFieldValue(this.FIELD_UOM_TYPE);
		const uom = fd.getFieldValue(this.FIELD_UNIT_OF_MEASURE);
		const attrName = fd.getFieldValue(xml.ATTR_NAME);
		const reflectionAttrOwner = fd.getFieldValue(this.FIELD_REFLECTION_ATTRIBUTE_OWNER);

		this.m_urlNode = url;

		this.createUOMAttribute(xml.ATTR_REF, csi.EMPTY_URL, UOM_SUFFIX, CLASS_UOM_CONVERSION);
		this.createUOMAttribute(xml.ATTR_DOUBLE, value, csi.EMPTY_STRING, null);

		this.m_writer.callMethod("SiteAdmin", "AddEntriesToUOMModuleCreateLists")
			.addParameter("UOMType", xml.ATTR_REF, uomType)
			.addParameter("UnitOfMeasure", xml.ATTR_REF, uom)
			.addParameter("AttributeName", xml.ATTR_STRING, attrName)
			.addParameter("ReflectionAttributeOwner", xml.ATTR_STRING, reflectionAttrOwner);

		return true;
	},

	createUOMAttribute(type: string, value: any, suffix: string, targetClassName: string): void {
		this.createAttribute(type, value, suffix);

		const fd = this.m_formData;
		const id = fd.getFieldValue(xml.ATTR_NAME) + suffix;

		const cn = this.m_writer.changeNode(this.m_urlNode + suffix)
			.changeAttributeFlag(xml.ATTR_NAME, xml.ATTR_FLAG_MODIFIED_BY_CLIENT, true)
			.changeAttribute(xml.ATTR_NAME, xml.ATTR_STRING, id)
			.changeAttributeFlag(reflection.ATTR_VALUETYPE, xml.ATTR_FLAG_MODIFIED_BY_CLIENT, true)
			.changeAttribute(reflection.ATTR_VALUETYPE, xml.ATTR_STRING, type);

		if (type === xml.ATTR_REF) {
			const uomTypeName = this.m_cache.getNode(fd.getFieldValue(this.FIELD_UOM_TYPE), true).$Name;
			const uomName = getUOMNameFromUnitOfMeasureCNL(fd.getFieldValue(this.FIELD_UNIT_OF_MEASURE));
			const identityConversionURL = this.convertInitialValueToJSON(type, generateUOMConversionURL(uomTypeName, uomName, uomName));

			cn.changeAttributeFlag(reflection.ATTR_VALUE, xml.ATTR_FLAG_MODIFIED_BY_CLIENT, true);
			cn.changeAttribute(reflection.ATTR_VALUE, xml.ATTR_STRING, identityConversionURL);
			cn.changeAttributeFlag(BusinessObject.ATTR_TARGET_CLASS_NAME, xml.ATTR_FLAG_MODIFIED_BY_CLIENT, true);
			cn.changeAttribute(BusinessObject.ATTR_TARGET_CLASS_NAME, xml.ATTR_STRING, targetClassName);
			cn.changeAttributeFlag(reflection.ATTR_FORMAT, xml.ATTR_FLAG_MODIFIED_BY_CLIENT, true);
			cn.changeAttribute(reflection.ATTR_FORMAT, xml.ATTR_STRING, csi.EMPTY_STRING);
			cn.changeAttributeFlag(this.FIELD_NOTES, xml.ATTR_FLAG_MODIFIED_BY_CLIENT, true);
			cn.changeAttribute(this.FIELD_NOTES, xml.ATTR_STRING, csi.EMPTY_STRING);
			cn.changeAttributeFlag(reflection.ATTR_COLUMN_WIDTH, xml.ATTR_FLAG_MODIFIED_BY_CLIENT, true);
			cn.changeAttribute(reflection.ATTR_COLUMN_WIDTH, xml.ATTR_ENUM, xml.enumCreate(this.COLUMNWIDTH_TYPE, "S"));
			cn.changeAttributeFlag(this.FIELD_VALIDATION_EXPRESSION, xml.ATTR_FLAG_MODIFIED_BY_CLIENT, true);
			cn.changeAttribute(this.FIELD_VALIDATION_EXPRESSION, xml.ATTR_STRING, csi.EMPTY_STRING);
			cn.changeAttributeFlag(reflection.ATTR_EXP, xml.ATTR_FLAG_MODIFIED_BY_CLIENT, true);
			cn.changeAttribute(reflection.ATTR_EXP, xml.ATTR_STRING, csi.EMPTY_STRING);
			cn.changeAttributeFlag(this.FIELD_EXPRESSION_STATE, xml.ATTR_FLAG_MODIFIED_BY_CLIENT, true);
			cn.changeAttribute(this.FIELD_EXPRESSION_STATE, xml.ATTR_ENUM, xml.enumCreate(this.FIELD_EXPRESSION_STATE, csi.EMPTY_STRING));
			cn.changeAttributeFlag(this.FIELD_REQUIRED, xml.ATTR_FLAG_MODIFIED_BY_CLIENT, true);
			cn.changeAttribute(this.FIELD_REQUIRED, xml.ATTR_BOOLEAN, true);

			// disable row editing for UOM conversion attribute
			this.m_writer.changeNodeFlags(this.m_urlNode + suffix, xml.NODE_FLAG_NOEDIT);
		}

		// use Attribute Name if Display Name is not provided
		if (fd.getFieldValue("LocaleName") === csi.EMPTY_STRING) {
			const form = this.popupView as any as FormWidget;
			form.setFieldValue("LocaleName", fd.getFieldValue(xml.ATTR_NAME));
		}

		// append " UOM" to the conversion attribute Display Name
		if (suffix === UOM_SUFFIX) {
			this.toWriterLocale(true, id, " UOM");
		} else {
			this.toWriterLocale(true, id, csi.EMPTY_STRING);
		}
	},
});

export = NewUOMAttributes;